import { useEffect, useState } from "react";
import { Crown, Lock, Sparkles, ExternalLink, Check, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const PremiumLock = ({ feature = "AI feature", className = "", inline = false }) => {
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("idle"); // idle | paid

  useEffect(() => {
    api.get("/billing/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const goPay = () => {
    if (!cfg?.payment_link_url) {
      toast.error("Payment link not configured yet — ask the owner.");
      return;
    }
    window.open(cfg.payment_link_url, "_blank", "noopener,noreferrer");
    setStep("paid");
  };

  const claimUpgrade = async (tier = "squad") => {
    setBusy(true);
    try {
      await api.post("/billing/request-upgrade", { tier, note: "Paid via Chase Pay-by-Link" });
      toast.success("Request sent · the owner will approve shortly.");
      setStep("idle");
    } catch {
      toast.error("Couldn't send request");
    } finally {
      setBusy(false);
    }
  };

  // Owner / dev demo unlock (kept for testing)
  const demoUnlock = async () => {
    setBusy(true);
    try {
      await api.post("/billing/upgrade");
      await refresh();
      toast.success("Squad upgraded · premium unlocked");
    } catch {
      toast.error("Upgrade failed");
    } finally {
      setBusy(false);
    }
  };

  if (inline) {
    return (
      <button onClick={goPay} className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] hover:underline ${className}`} data-testid="premium-lock-inline">
        <Lock className="w-3 h-3" /> Premium · upgrade to unlock {feature}
      </button>
    );
  }

  const business = cfg?.business || { legal_name: "Global Relocation LLC", dba: "Monster Squad", env: "sandbox" };

  return (
    <div className={`relative border border-[#E60000]/30 bg-[#E60000]/5 p-6 ${className}`} data-testid="premium-lock">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-[#E60000] flex items-center justify-center shrink-0">
          <Crown className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="font-heading font-black tracking-tight">{feature} is premium</div>
          <p className="text-sm text-black/60 mt-1 max-w-md">
            AI search, auto-tags, auto-captions, ghost-prompts, Scratch FX and vlog templates unlock with squad premium.
          </p>

          {cfg?.tiers?.length > 0 && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {cfg.tiers.map((t) => (
                <div key={t.id} className="border border-black/15 bg-white p-4" data-testid={`tier-${t.id}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="font-heading font-black tracking-tight">{t.label}</div>
                    <div className="font-display font-black text-2xl tracking-tighter">${(t.price_cents / 100).toFixed(0)}<span className="text-xs font-mono font-normal text-black/40">/mo</span></div>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {t.features.map((f) => (
                      <li key={f} className="text-xs text-black/65 flex gap-1.5"><Check className="w-3 h-3 text-[#E60000] mt-0.5 shrink-0" />{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {step === "idle" ? (
              <>
                <Button
                  onClick={goPay}
                  disabled={!cfg?.configured}
                  className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white"
                  data-testid="premium-pay-button"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" /> Pay via Chase
                  <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={demoUnlock}
                  disabled={busy}
                  className="rounded-sm border-black/20"
                  data-testid="premium-demo-unlock"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Demo unlock (no payment)
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm text-black/65">Done paying on Chase?</span>
                <Button
                  onClick={() => claimUpgrade("squad")}
                  disabled={busy}
                  className="rounded-sm bg-black hover:bg-[#E60000] text-white"
                  data-testid="premium-claim-button"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} I've paid · request approval
                </Button>
                <Button variant="ghost" onClick={() => setStep("idle")} className="rounded-sm" data-testid="premium-cancel-claim">Cancel</Button>
              </>
            )}
          </div>

          {!cfg?.configured && user?.role !== "owner" && (
            <p className="mt-3 text-xs text-[#E60000] font-medium">
              Pay link not yet published by owner — try Demo unlock for now.
            </p>
          )}
          <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-mono text-black/40 flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" />
            Processed by Chase QuickAccept · {business.legal_name} dba {business.dba} · {business.env}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PremiumBadge = ({ user }) => {
  if (!user) return null;
  if (user.role === "owner") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 bg-[#E60000] text-white" data-testid="badge-owner">
        <Crown className="w-3 h-3" /> Owner
      </span>
    );
  }
  if (user.is_premium) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 bg-black text-white" data-testid="badge-premium">
        <Sparkles className="w-3 h-3" /> Premium
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 border border-black/20 text-black/60" data-testid="badge-free">
      Free
    </span>
  );
};
